import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Sparkles, Facebook, Instagram, Twitter, MessageCircle, Music2, Youtube } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PostImageCard } from "./PostImageCard";

const HANDLE = "@OkuapemmanMA";
const TAGS = "#Okuapem #Okuapemman #GhanaiansInMassachusetts #OfieBiakoye";
const clip = (s, n) => (s.length <= n ? s : `${s.slice(0, n - 1).trimEnd()}…`);
const fmtDate = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "");

function build(src, site) {
  const { title, date, location, body, link } = src;
  const when = date ? `📅 ${fmtDate(date)}` : "";
  const where = location ? `📍 ${location}` : "";
  const meta = [when, where].filter(Boolean).join("\n");
  const url = `${site}${link}`;
  const lead = body || "";
  return [
    { key: "facebook", name: "Facebook", Icon: Facebook, color: "#1877F2", max: 63206,
      text: `${title}\n\n${lead}\n\n${meta}\n\nAll sons and daughters of Okuapem — and friends — are welcome. Members can RSVP in the portal.\n👉 ${url}\n\nOfie! Biakoye! 🇬🇭` },
    { key: "instagram", name: "Instagram", Icon: Instagram, color: "#E1306C", max: 2200,
      text: `${title} 🥁✨\n\n${clip(lead, 400)}\n\n${meta}\n\nRSVP link in bio → ${url}\n.\n.\n.\n${TAGS} #Ghana #Boston #Worcester #Akan #Odwira` },
    { key: "twitter", name: "X (Twitter)", Icon: Twitter, color: "#0F1419", max: 280,
      text: clip(`${title}\n${meta}\n${url}\n#Okuapem #OfieBiakoye`, 280) },
    { key: "whatsapp", name: "WhatsApp", Icon: MessageCircle, color: "#25D366", max: 65536,
      text: `*${title}*\n\n${lead}\n\n${meta}\n\nPlease RSVP and share with family: ${url}\n\n_Okuapemman Association of Massachusetts — Ofie! Biakoye!_` },
    { key: "tiktok", name: "TikTok", Icon: Music2, color: "#010101", max: 2200,
      text: clip(`${title} 🥁 ${when ? fmtDate(date) : ""} ${location ? `· ${location}` : ""}\nCome through! Link in bio 🔗\n${TAGS} #Ghana #fyp #Akan`, 300) },
    { key: "youtube", name: "YouTube (description)", Icon: Youtube, color: "#FF0000", max: 5000,
      text: `${title}\n\n${lead}\n\n${meta}\n\nOkuapemman Association of Massachusetts — uniting sons and daughters of Okuapem in New England since 2010.\nWebsite: ${site}\nRSVP: ${url}\nFollow us: ${HANDLE}\n\n${TAGS}` },
  ];
}

function CaptionCard({ c }) {
  const [ok, setOk] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(c.text); setOk(true); toast.success(`${c.name} caption copied`); setTimeout(() => setOk(false), 1500); } catch { toast.error("Copy failed"); } };
  const over = c.text.length > c.max;
  return (
    <div className="rounded-xl border border-gold/30 bg-parchment-card p-4" data-testid={`caption-${c.key}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><span className="flex h-7 w-7 items-center justify-center rounded-lg text-white" style={{ background: c.color }}><c.Icon size={14} /></span><span className="text-sm font-bold">{c.name}</span></div>
        <div className="flex items-center gap-3">
          <span className={`font-mono-accent text-[11px] ${over ? "text-destructive" : "text-muted-foreground"}`} data-testid={`caption-len-${c.key}`}>{c.text.length}/{c.max}</span>
          <button onClick={copy} className="inline-flex items-center gap-1 rounded-full bg-crimson px-3 py-1 text-xs font-semibold text-parchment hover:bg-crimson-hover" data-testid={`caption-copy-${c.key}`}>{ok ? <Check size={12} /> : <Copy size={12} />} Copy</button>
        </div>
      </div>
      <pre className="mt-2 whitespace-pre-wrap font-sans text-xs text-foreground/85">{c.text}</pre>
    </div>
  );
}

export function SocialCaptionGenerator() {
  const { data: events } = useApi("/events");
  const { data: anns } = useApi("/announcements");
  const [pick, setPick] = useState("");
  const [extra, setExtra] = useState("");
  const sources = useMemo(() => [
    ...(events || []).map((e) => ({ id: `e:${e.id}`, kind: "Event", title: e.title, date: e.date, location: e.location || "", body: e.description || "", link: `/events/${e.id}` })),
    ...(anns || []).map((a) => ({ id: `a:${a.id}`, kind: "Announcement", title: a.title, date: "", location: "", body: a.body || "", link: "/portal/announcements" })),
  ], [events, anns]);
  const src = sources.find((s) => s.id === pick);
  const captions = src ? build({ ...src, body: [src.body, extra].filter(Boolean).join("\n\n") }, window.location.origin) : [];
  return (
    <div className="glass rounded-2xl p-6 space-y-4" data-testid="caption-generator">
      <div>
        <h3 className="flex items-center gap-2 font-serif-display text-xl font-bold text-crimson"><Sparkles size={18} className="text-gold" /> Social post generator</h3>
        <p className="text-xs text-muted-foreground">Pick an event or announcement — get a ready-to-paste caption sized for every platform, with dates, location, RSVP link and hashtags.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div><Label>Source</Label>
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger data-testid="caption-source"><SelectValue placeholder="Choose an event or announcement" /></SelectTrigger>
            <SelectContent>{sources.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`caption-src-${s.id.replace(":", "-")}`}>{s.kind}: {s.title}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Extra line (optional)</Label><Textarea rows={1} placeholder="e.g. Bring a dish to share!" value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="caption-extra" /></div>
      </div>
      {src && <PostImageCard label={src.kind === "Event" ? "You're invited" : "Important Announcement"} title={src.title} dateText={fmtDate(src.date)} location={src.location} site={window.location.origin} />}
      {src && <div className="grid gap-3 lg:grid-cols-2" data-testid="caption-list">{captions.map((c) => <CaptionCard key={c.key} c={c} />)}</div>}
    </div>
  );
}
